import { stripDuplicateTrailingHashtags } from '@mondaygpt/constants'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components'
import { Loader2, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTikTokCreatorInfo } from '../hooks/useTikTokCreatorInfo'
import type { TikTokPostSettings, TikTokVideoContent } from '../types'
import { TikTokPostSettingsFields } from './TikTokPostSettingsFields'
import {
  EMPTY_TIKTOK_POST_SETTINGS,
  isCompleteTikTokPostSettings,
  type TikTokPostSettingsDraft,
} from '../utils/tiktok-post-settings'

interface TikTokPostDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  content: TikTokVideoContent
  isPublishing: boolean
  publishError: string | null
  /** Called with the completed settings; the caller persists them and publishes. */
  onPost: (settings: TikTokPostSettings) => void
  /** Primary button label. Defaults to "Post to TikTok"; the schedule path uses "Save and schedule". */
  ctaLabel?: string
}

function initialDraft(content: TikTokVideoContent): TikTokPostSettingsDraft {
  return content.postSettings ? { ...content.postSettings } : { ...EMPTY_TIKTOK_POST_SETTINGS }
}

/**
 * The "Post to TikTok" step TikTok's Content Sharing Guidelines require before
 * a Direct Post: who the video is going to (creator_info nickname + avatar), a
 * preview of the video and its title, the audience picked from the creator's
 * own options with no default, interaction toggles, commercial-content
 * disclosure, the consent line and the processing notice. Nothing is sent to
 * TikTok until the user presses Post here.
 */
export function TikTokPostDialog({
  open,
  onOpenChange,
  content,
  isPublishing,
  publishError,
  onPost,
  ctaLabel = 'Post to TikTok',
}: TikTokPostDialogProps) {
  const { info, loading, error } = useTikTokCreatorInfo(open)
  const [draft, setDraft] = useState<TikTokPostSettingsDraft>(() => initialDraft(content))
  const [videoDurationSec, setVideoDurationSec] = useState<number | null>(null)

  // Start from the saved choices every time the dialog opens. Read the content
  // through a ref so a content update while the dialog is open (a Pusher
  // patch, the settings persisting) never resets choices in progress.
  const contentRef = useRef(content)
  contentRef.current = content
  useEffect(() => {
    if (open) setDraft(initialDraft(contentRef.current))
  }, [open])

  // A saved audience the creator can no longer post to (account went private,
  // etc.) must not be sent — clear it so the user picks again from the live list.
  useEffect(() => {
    if (!info || !draft.privacyLevel) return
    if (!info.privacyLevelOptions.includes(draft.privacyLevel)) {
      setDraft(d => ({ ...d, privacyLevel: undefined }))
    }
  }, [info, draft.privacyLevel])

  const title = useMemo(() => {
    const caption = stripDuplicateTrailingHashtags(content.caption ?? '', content.hashtags ?? [])
    const tags = (content.hashtags ?? []).slice(0, 5).map(h => (h.startsWith('#') ? h : `#${h}`))
    return [caption, ...tags].filter(Boolean).join(' ')
  }, [content.caption, content.hashtags])

  const tooLong =
    info?.maxVideoPostDurationSec != null &&
    videoDurationSec != null &&
    videoDurationSec > info.maxVideoPostDurationSec
  const canPost =
    !isPublishing && !loading && !error && !tooLong && isCompleteTikTokPostSettings(draft)
  const aiGenerated = content.videoSource !== 'uploaded'
  const creatorLine = info?.creatorUsername
    ? `@${info.creatorUsername}${info.creatorNickname ? ` (${info.creatorNickname})` : ''}`
    : info?.creatorNickname

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" variant="dark" withCloseButton>
        <DialogHeader>
          <DialogTitle variant="dark">Post to TikTok</DialogTitle>
          <DialogDescription variant="dark">
            {creatorLine ? (
              <span className="flex items-center gap-2">
                {info?.creatorAvatarUrl && (
                  <img
                    src={info.creatorAvatarUrl}
                    alt=""
                    className="h-6 w-6 rounded-full object-cover"
                  />
                )}
                <span>
                  Posting to <span className="font-medium text-paper">{creatorLine}</span>
                </span>
              </span>
            ) : (
              'Review the video and choose how it is shared on TikTok.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-[168px_1fr]">
          {/* Preview: the video + the title TikTok will receive. */}
          <div className="space-y-2">
            <div className="aspect-[9/16] w-full overflow-hidden rounded-lg border border-line-bright bg-bg-3">
              {content.videoUrl ? (
                <video
                  src={content.videoUrl}
                  className="h-full w-full object-cover"
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={e =>
                    setVideoDurationSec(
                      Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : null
                    )
                  }
                />
              ) : (
                <div className="flex h-full items-center justify-center text-[12px] text-paper-faint">
                  No video
                </div>
              )}
            </div>
            <div>
              <div className="text-[12px] font-medium text-paper">Caption</div>
              <p className="line-clamp-4 text-[12px] break-words text-paper-dim">
                {title || 'No caption yet. Add one in the editor.'}
              </p>
              <p className="text-[11px] text-paper-faint">
                Edit the caption and hashtags in the editor.
              </p>
            </div>
            {tooLong && (
              <p className="text-[12px] text-[color:var(--alert-error-text)]">
                This video is longer than the {info?.maxVideoPostDurationSec}s TikTok allows for
                your account.
              </p>
            )}
          </div>

          <TikTokPostSettingsFields
            draft={draft}
            onChange={setDraft}
            creatorInfo={info}
            creatorInfoLoading={loading}
            creatorInfoError={error}
            aiGenerated={aiGenerated}
          />
        </div>

        {publishError && (
          <p className="text-center text-[13px] text-[color:var(--alert-error-text)]">
            {publishError}
          </p>
        )}

        <Button
          variant="t3-primary"
          size="t3-md"
          className="w-full"
          onClick={() => {
            if (isCompleteTikTokPostSettings(draft)) onPost(draft)
          }}
          disabled={!canPost}
        >
          {isPublishing ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Posting...
            </>
          ) : (
            <>
              <Send size={16} />
              {ctaLabel}
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
